/**
 * MYB-276: Leave self-service API calls
 * Covers: types, balance, submit/cancel requests, manager review, team calendar.
 */
import { api } from './client';

export interface LeaveType {
  id: number;
  code: string;
  name: string;
  annual_days: number;
  requires_balance: boolean;
  half_day_allowed: boolean;
  is_active: boolean;
}

export interface LeaveBalance {
  employee_id: string;
  leave_type_code: string;
  leave_type_name: string;
  year: number;
  granted: number;
  used: number;
  remaining: number;
}

export interface LeaveRequest {
  id: number;
  employee_id: string;
  leave_type_code: string;
  leave_type_name: string;
  start_date: string;
  end_date: string;
  days: number;
  half_day_type: string | null;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  reviewer_id: string | null;
  review_comment: string | null;
  reviewed_at: number | null;
  created_at: number;
  updated_at: number;
  // Manager-only fields (pending approvals)
  employee_name?: string;
  employee_department?: string;
}

export interface SubmitLeaveRequest {
  leave_type_code: string;
  start_date: string;
  end_date: string;
  half_day_type?: 'am' | 'pm' | null;
  reason?: string;
}

export interface ReviewLeaveRequest {
  action: 'approved' | 'rejected';
  comment?: string;
}

export const leaveRequestsApi = {
  /** 전체 휴가 유형 */
  getLeaveTypes: async (): Promise<LeaveType[]> => {
    const res = await api.get('/api/v1/leave/types');
    return res.data?.data ?? res.data;
  },

  /** 내 휴가 잔액 */
  getMyBalance: async (year?: number): Promise<LeaveBalance[]> => {
    const params: any = {};
    if (year) params.year = year;
    const res = await api.get('/api/v1/leave/balance', { params });
    return res.data?.data ?? res.data;
  },

  /** 특정 직원 잔액 (관리자) */
  getEmployeeBalance: async (employeeId: string, year?: number): Promise<LeaveBalance[]> => {
    const params: any = {};
    if (year) params.year = year;
    const res = await api.get(`/api/v1/leave/balance/${encodeURIComponent(employeeId)}`, { params });
    return res.data?.data ?? res.data;
  },

  /** 휴가 신청 */
  submitRequest: async (data: SubmitLeaveRequest): Promise<{ id: number; days: number; status: string }> => {
    const res = await api.post('/api/v1/leave/requests', data);
    return res.data?.data ?? res.data;
  },

  /** 내 휴가 이력 */
  getMyRequests: async (params?: { status?: string; year?: number }): Promise<LeaveRequest[]> => {
    const res = await api.get('/api/v1/leave/requests', { params });
    return res.data?.data ?? res.data;
  },

  /** 신청 취소 */
  cancelRequest: async (requestId: number): Promise<void> => {
    await api.delete(`/api/v1/leave/requests/${requestId}`);
  },

  /** 결재 대기 목록 (관리자) */
  getPendingApprovals: async (): Promise<LeaveRequest[]> => {
    const res = await api.get('/api/v1/leave/requests/pending');
    return res.data?.data ?? res.data;
  },

  /** 결재 처리 (관리자) */
  reviewRequest: async (requestId: number, data: ReviewLeaveRequest): Promise<void> => {
    await api.put(`/api/v1/leave/requests/${requestId}/review`, data);
  },

  /** 팀 캘린더 */
  getTeamCalendar: async (year: number, month: number): Promise<LeaveRequest[]> => {
    const res = await api.get('/api/v1/leave/team', { params: { year, month } });
    return res.data?.data ?? res.data;
  },

  /** 연차 법정 부여일수 계산 (근로기준법 제60조) — admin/hr_manager only */
  calculateEntitlement: async (employeeId: string, referenceDate?: string): Promise<{
    entitlement_days: number;
    years_of_service: number;
    hire_date: string;
    reference_date: string;
  }> => {
    const params: Record<string, string> = { employee_id: employeeId };
    if (referenceDate) params.reference_date = referenceDate;
    const res = await api.get('/api/v1/leave/calculate-entitlement', { params });
    return res.data?.data ?? res.data;
  },
};
